import type { ModuleId } from "../core/RunState.ts";
import type { CombatSystem } from "./CombatSystem.ts";
import type { DraftSystem } from "./DraftSystem.ts";
import type { ScoreSystem } from "./ScoreSystem.ts";
import type { EconomySystem } from "./EconomySystem.ts";
import type { SynergySystem } from "./SynergySystem.ts";
import type { RuntimeState } from "../game/types.ts";
import { gainLives } from "../game/stateIntents.ts";
import type { ModuleEffect } from "./ModuleRegistry.ts";
import { getModule } from "./ModuleRegistry.ts";

/**
 * Context required to apply module effects to game systems.
 */
export interface ModuleContext {
  gs: RuntimeState;
  combatSystem: CombatSystem;
  draftSystem: DraftSystem;
  scoreSystem: ScoreSystem;
  economySystem: EconomySystem;
  synergySystem: SynergySystem;
}

export interface AppliedEffect {
  module: ModuleId;
  /** i18n key of the module's effect description (module.<id>.desc). */
  description: string;
}

interface ModuleEffectDef {
  apply: (value: number, id: ModuleId, ctx: ModuleContext) => void;
}

/**
 * Module-effect registry (BL-90 / D-219) — the open-closed dispatch map,
 * mirroring the cards' `CARD_EFFECTS`. Keyed by `ModuleEffect.type`; the
 * numbers live in `public/data/modules.json` and arrive here as `value`,
 * which is applied VERBATIM (the determinism contract — no arithmetic
 * between data and system). A new module with an existing effect type is
 * a pure JSON row; a new effect type adds one entry here.
 */
/**
 * The COMPOSITION MODE of every effect axis — the answer to "what happens if
 * two selected modules touch the same thing?".
 *
 * The 2026-08-16 gameplay-effect audit found this was answered only by reading
 * each `apply()` and noticing whether it wrote `*=` or `=`. Six of the twelve
 * axes assign, so a second module on the same axis would have silently won,
 * with no test and no warning — a last-write-wins trap that today's catalogue
 * happens not to spring because no two modules share a `set` axis.
 *
 *  - `compose` — the contribution multiplies into whatever is already there
 *    (`CombatSystem.applyRangeMultiplier` is the documented F-03 home for this
 *    rule). Several modules on this axis stack, in any order, correctly.
 *  - `set` — the contribution ASSIGNS. Exactly ONE module may carry such an
 *    axis; a second would overwrite the first depending on catalogue order.
 *  - `flag` — the contribution raises a boolean or is read elsewhere entirely.
 *    Idempotent, so sharing is harmless.
 *
 * `tests/data/ModuleAxisInvariant.test.ts` enforces the "at most one module per
 * `set` axis" invariant against `modules.json`, so adding a second module on a
 * `set` axis fails the build instead of quietly changing balance. Adding a NEW
 * effect type will not compile until it is classified here.
 */
export const MODULE_EFFECT_COMPOSITION: Record<
  ModuleEffect["type"],
  "compose" | "set" | "flag"
> = {
  starting_credits: "compose", // `gold += amount` — additive, stacks
  range_multiplier: "compose", // `*=` (F-03)
  score_multiplier: "set",
  synergy_multiplier: "set",
  free_first_tower: "flag",
  revive: "flag",
  draft_draw_count: "set",
  extra_hp: "compose", // `gainLives` adds
  combo_timeout_multiplier: "set",
  skill_slots: "set",
  boss_damage_multiplier: "set",
  auto_degrade_threshold: "flag",
};

export const MODULE_EFFECTS: Record<ModuleEffect["type"], ModuleEffectDef> = {
  starting_credits: {
    apply: (value, id, ctx) =>
      ctx.economySystem.grantStartingCredits(value, `module:${id}`),
  },
  range_multiplier: {
    apply: (value, _id, ctx) => ctx.combatSystem.applyRangeMultiplier(value),
  },
  score_multiplier: {
    apply: (value, _id, ctx) => {
      ctx.scoreSystem.scoreMultiplier = value;
    },
  },
  synergy_multiplier: {
    apply: (value, _id, ctx) => {
      ctx.synergySystem.effectMultiplier = value;
    },
  },
  free_first_tower: {
    apply: (_value, _id, ctx) => {
      ctx.economySystem.freeFirstTower = true;
    },
  },
  revive: {
    apply: () => {
      // Revive handled by eventHandlers — reads the flag via hasFailsafe().
    },
  },
  draft_draw_count: {
    apply: (value, _id, ctx) => {
      ctx.draftSystem.drawCount = value;
    },
  },
  extra_hp: {
    apply: (value, _id, ctx) => {
      gainLives(ctx.gs, value);
    },
  },
  combo_timeout_multiplier: {
    apply: (value, _id, ctx) => {
      ctx.scoreSystem.comboTimeoutMultiplier = value;
    },
  },
  skill_slots: {
    apply: (value, _id, ctx) => {
      ctx.draftSystem.maxSlotOverride = value;
    },
  },
  boss_damage_multiplier: {
    apply: (value, _id, ctx) => {
      ctx.combatSystem.bossDamageReduction = value;
    },
  },
  auto_degrade_threshold: {
    apply: () => {
      // Deliberate no-op AT APPLY TIME, like `revive`: the threshold is not a
      // property of any system in `ModuleContext`. `startRun` resolves it from
      // the committed selection and pushes it into the PerfGovernor
      // (`main.ts`, right beside `failsafeAvailable`), which is the only object
      // that owns the degrade decision — see `getAutoDegradeThreshold` below.
      //
      // The old comment here pointed at `WebGL2Effects`, which never read it;
      // the governor took that decision over long ago and the pointer was
      // never updated. That stale pointer is exactly why the module shipped
      // DEAD for months.
    },
  },
};

/**
 * Apply selected module effects to game systems at run start.
 * Pure orchestration — called ONCE, effects persist for the run.
 */
export function applyModulesToSystems(
  modules: ModuleId[],
  ctx: ModuleContext,
): AppliedEffect[] {
  const applied: AppliedEffect[] = [];

  for (const id of modules) {
    const module = getModule(id);
    if (!module) continue;
    const def = MODULE_EFFECTS[module.effect.type];
    if (!def) continue;
    def.apply(module.effect.value, id, ctx);
    applied.push({ module: id, description: module.description });
  }

  return applied;
}

/**
 * Check if a set of modules includes failsafe_protocol (for revive logic).
 */
export function hasFailsafe(modules: ModuleId[]): boolean {
  return modules.includes("failsafe_protocol");
}

/**
 * The frame budget (ms) the PerfGovernor's DEGRADE decision measures against
 * for a run: 14 with `adaptive_throttling`, otherwise the standard 16.
 *
 * These are the module's own player-facing numbers ("Quality reduces at 14ms",
 * both locales) and `PERFORMANCE.MAX_FRAME_TIME_MS`. Called once per run from
 * `startRun`; `PerfGovernor.reset()` restores the default, so nothing leaks
 * between runs.
 */
export function getAutoDegradeThreshold(modules: ModuleId[]): number {
  return modules.includes("adaptive_throttling") ? 14 : 16;
}
